import React, { ChangeEvent } from 'react'
import {
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Col,
  Color,
  Display,
  FlexDirection,
  Grid,
  Icon,
  IconVariant,
  RadioGroup,
  Space,
} from '@gousto-internal/citrus-react'

import type {
  LoggedOutBoxSummaryActions,
  LoggedOutBoxSummaryPortionSize,
  LoggedOutBoxSummaryPortionSizeView,
} from '../types'
import { PanelContinueButton } from './PanelContinueButton'
import { portionSizeIcons } from './PortionSizeIcons'

type PortionSizePanelProps = {
  view: LoggedOutBoxSummaryPortionSizeView
  actions: Pick<LoggedOutBoxSummaryActions, 'onPortionSizeSelect' | 'onPortionSizeConfirm'>
}

export const PortionSizePanel = ({ view, actions }: PortionSizePanelProps) => {
  const isConfirmDisabled = view.confirmCta.isDisabled || view.confirmCta.isPending

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value) as LoggedOutBoxSummaryPortionSize
    actions.onPortionSizeSelect(value)
  }

  const handleConfirm = () => {
    if (!isConfirmDisabled) {
      actions.onPortionSizeConfirm()
    }
  }

  const options = view.availablePortionSizes.map((option) => {
    const PortionIcon = portionSizeIcons[option.value]

    return {
      name: 'portionSize',
      value: option.value,
      label: () => (
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          paddingH={3}
          paddingRight={0}
          data-testid={`logged-out-box-summary-portion-size-option-${option.value}`}
        >
          <Grid alignItems={AlignItems.Center}>
            <Col size={11}>
              <Body2 color={Color.ColdGrey_900}>
                <Bold>{option.label}</Bold>
              </Body2>
            </Col>
            <Col size={1}>
              <Box display={Display.Flex}>
                <PortionIcon />
              </Box>
            </Col>
          </Grid>
          {option.isSelected && (
            <Box paddingV={2} paddingBottom={0}>
              <Body2 color={Color.ColdGrey_800}>
                {'Choose from '}
                <Bold>{option.details.recipeRangeLabel}</Bold>
                {` ${option.details.recipeRangeSuffix}`}
              </Body2>
              <Space size={1} direction="vertical" />
              <Body2 color={Color.ColdGrey_800}>Suitable for:</Body2>
              <Space size={1} direction="vertical" />
              {option.details.suitableFor.map((item) => (
                <Box key={item} display={Display.Flex} alignItems={AlignItems.Center}>
                  <Icon name="tick" variant={IconVariant.Confirmation} size={4} />
                  <Space direction="horizontal" size={2} />
                  <Body2 color={Color.ColdGrey_800}>{item}</Body2>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ),
    }
  })

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      paddingH={3}
      paddingBottom={6}
      data-testid="logged-out-box-summary-portion-size-panel"
    >
      <RadioGroup
        value={view.selectedPortionSize}
        outline
        alignItems={AlignItems.FlexStart}
        options={options}
        onChange={handleChange}
      />
      {view.infoBanner && (
        <>
          <Space size={3} direction="vertical" />
          <Box
            bg={Color.Informative_50}
            display={Display.Flex}
            paddingH={4}
            paddingV={4}
            alignItems={AlignItems.FlexStart}
            borderColor={Color.Informative_50}
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderRadius={1.5}
            gap={2}
            data-testid="logged-out-box-summary-portion-size-info-banner"
          >
            <Icon name="info" variant={IconVariant.Informative} />
            <Body2 color={Color.ColdGrey_800}>
              <Bold>Heads up: </Bold>
              {view.infoBanner}
            </Body2>
          </Box>
        </>
      )}
      {view.warningMessage && (
        <>
          <Space size={3} direction="vertical" />
          <Box
            bg={Color.Warning_50}
            display={Display.Flex}
            paddingH={4}
            paddingV={4}
            alignItems={AlignItems.FlexStart}
            borderColor={Color.Warning_100}
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderRadius={1.5}
            gap={2}
            data-testid="logged-out-box-summary-portion-size-warning"
          >
            <Icon name="warning" variant={IconVariant.Error} />
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
              <Body2 color={Color.ColdGrey_800}>
                <Bold>{view.warningMessage.title}</Bold>
              </Body2>
              <Body2 color={Color.ColdGrey_800}>
                {'Remove '}
                <Bold>{view.warningMessage.recipesToRemove}</Bold>
                {` ${
                  view.warningMessage.recipesToRemove === 1 ? 'recipe' : 'recipes'
                } before you check out.`}
              </Body2>
            </Box>
          </Box>
        </>
      )}
      <Space size={4} direction="vertical" />
      <PanelContinueButton
        cta={view.confirmCta}
        testId="logged-out-box-summary-portion-size-confirm"
        onClick={handleConfirm}
      />
    </Box>
  )
}
